import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography, Divider } from '@mui/material';
import VpLiveChatButton from './VpLiveChatButton';


function matchAnswer(q) {
    const s = (q || '').trim().toLowerCase();
    const has = (...words) => words.some((w) => s.includes(w));

    if (!s) return `Type a question like: "totp", "set pin", "dispute", "purchase", or "maps".`;

    if (has('create account', 'sign up', 'signup', 'register', 'new account', 'activation', 'sms')) {
        return `Create account / activation:
1) Click Sign Up
2) Enter your information
3) Verify the SMS code (Activation)
4) Set up Authenticator (TOTP)
5) Login using username/password + 6-digit Authenticator code`;
    }

    if (has('totp', 'authenticator', 'mfa', '2fa', 'google authenticator')) {
        return `Authenticator (TOTP):
1) On Setup-TOTP screen, scan the QR code with Google Authenticator (or similar)
2) Enter the 6-digit code to confirm
3) At login, enter the current 6-digit code from the app`;
    }

    if (has('pin', 'temp pin', 'employee pin')) {
        return `Employee PIN:
1) Go to Employee PIN / PIN section
2) Enter Company Code
3) Enter Master PIN
4) Set Temp PIN + Expiration Date
5) Save/Update. Give Temp PIN + Expiration to the verifier`;
    }

    if (has('dispute', 'file a dispute', 'incorrect', 'wrong data')) {
        return `Dispute a verification:
1) Open the Dispute Form
2) Complete required fields
3) Upload supporting documentation
4) Submit. Your dispute is tracked and handled in an FCRA-aligned workflow`;
    }

    if (has('purchase', 'credit card', 'pay', 'payment', 'buy')) {
        return `Purchase a verification (Verifier):
1) Go to Purchase Verification
2) Enter employee + company details
3) Pay by credit card
4) View verification results and any uploaded documents`;
    }

    if (has('map', 'maps', 'geo', 'geolocation', 'state', 'usa map')) {
        return `Maps & geo:
- State map shows verification volume by state/region.
- Geo map shows approximate verifier activity by IP location.
- Employers use this for auditing, fraud monitoring, and trends.`;
    }

    if (has('fcra')) {
        return `FCRA:
Verification Professional follows FCRA-aligned processes for access control, auditing, disputes, and proper handling of consumer information.`;
    }

    return `I didn’t match that question yet.
Try: "totp", "set pin", "dispute", "purchase", "maps", or "create account".`;
}

export default function VpHelpChat() {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState('');
    const [messages, setMessages] = useState(() => [
        {
            role: 'bot',
            text: `Hi! I can help with account setup, TOTP, employee PIN, disputes, purchases, maps, and FCRA.`
        }
    ]);

    // ✅ auto-scroll to newest message
    const bottomRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages, open]);

    const quick = useMemo(
        () => ['Create account', 'TOTP', 'Set PIN', 'Dispute', 'Purchase', 'Maps', 'FCRA'],
        []
    );

    const send = (q) => {
        const userText = (q ?? text).trim();
        if (!userText) return;

        const ans = matchAnswer(userText);

        setMessages((prev) => [
            ...prev,
            { role: 'user', text: userText },
            { role: 'bot', text: ans }
        ]);

        setText(''); // ✅ clear input after send
    };

    return (
        <>
            {/* Floating Button */}
            <Button
                variant="contained"
                onClick={() => setOpen((v) => !v)}
                sx={{
                    position: 'fixed',
                    right: 20,
                    bottom: 20,
                    zIndex: 20000,

                    // ✅ Bigger button
                    minWidth: 110,
                    minHeight: 56,
                    px: 3,

                    // ✅ Rounded pill
                    borderRadius: 999,

                    // ✅ Yellow background + dark green text
                    backgroundColor: '#fde047', // yellow
                    color: '#14532d',           // dark green

                    fontSize: 16,
                    fontWeight: 700,
                    textTransform: 'none',

                    boxShadow: '0 8px 20px rgba(0,0,0,0.25)',

                    '&:hover': {
                        backgroundColor: '#facc15'
                    }
                }}
            >
                Help
            </Button>



            {/* Chat Panel */}
            {open ? (
                <Paper
                    elevation={10}
                    sx={{
                        position: 'fixed',
                        right: 18,
                        bottom: 74,
                        zIndex: 20000,
                        width: 360,
                        maxWidth: 'calc(100vw - 36px)',
                        height: 520, // ✅ fixed height so it never pushes the page
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        borderRadius: 3
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            bgcolor: '#22856d',
                            color: '#fff'
                        }}
                    >
                        <Typography variant="subtitle1">Help</Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <VpLiveChatButton variant="outlined" size="small" />


                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => setOpen(false)}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                                }}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>


                    {/* Quick buttons */}
                    <Box sx={{ px: 1.5, py: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                            {quick.map((q) => (
                                <Button
                                    key={q}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => send(q)}
                                    sx={{
                                        borderColor: '#22856d',
                                        color: '#22856d',
                                        '&:hover': { borderColor: '#1e745f', color: '#1e745f' }
                                    }}
                                >
                                    {q}
                                </Button>
                            ))}
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Messages area (scrollable) */}
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            px: 1.5,
                            py: 1.5,
                            backgroundColor: '#f8fbfa'
                        }}
                    >
                        {messages.map((m, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    mb: 1.25,
                                    display: 'flex',
                                    justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start'
                                }}
                            >
                                <Box
                                    sx={{
                                        maxWidth: '86%',
                                        px: 1.25,
                                        py: 1,
                                        borderRadius: 2,
                                        whiteSpace: 'pre-line',
                                        fontSize: 14,
                                        bgcolor: m.role === 'user' ? '#22856d' : 'white',
                                        color: m.role === 'user' ? 'white' : '#1f2937',
                                        border: m.role === 'user' ? 'none' : '1px solid #e5e7eb'
                                    }}
                                >
                                    {m.text}
                                </Box>
                            </Box>
                        ))}
                        <div ref={bottomRef} />
                    </Box>

                    <Divider />

                    {/* Input row (fixed at bottom) */}
                    <Box sx={{ p: 1.5 }}>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Ask about TOTP, PIN, dispute, purchase…"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        send();
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={() => send()}
                                sx={{ backgroundColor: '#22856d', '&:hover': { backgroundColor: '#1e745f' } }}
                            >
                                Send
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            ) : null}
        </>
    );
}
